import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUpDown, MoreHorizontal, SlidersHorizontal } from "lucide-react";
import type { CampaignListSortRule } from "@multi-chat/shared";
import { Button } from "../../components/ui/button/index.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu/index.js";
import { Popover, PopoverAnchor, PopoverContent } from "../../components/ui/popover/index.js";
import type { CampaignListDataColumnId } from "./campaign-list-table.js";
import {
  CAMPAIGN_LIST_MOBILE_PANEL_OPEN_MS,
  CAMPAIGN_LIST_MOBILE_TOOLS_CLOSED,
  campaignListMobileOpenOverflow,
  campaignListMobileOverflowClosed,
  campaignListMobileSelectOverflowItem,
  campaignListMobileSetPanel,
  type CampaignListMobilePanel,
  type CampaignListMobileToolsState,
} from "./campaign-list-toolbar-tools.js";
import { CampaignsColumnsPanel, CampaignsColumnsPopover } from "./CampaignsColumnsPopover.js";
import { CampaignsSortPanel, CampaignsSortPopover } from "./CampaignsSortPopover.js";

export function CampaignsListToolbarTools({
  sort,
  onSortChange,
  columnOrder,
  hiddenColumns,
  onToggleColumn,
  onMoveColumn,
  onReorderColumn,
  onResetColumns,
}: {
  sort: readonly CampaignListSortRule[];
  onSortChange: (next: CampaignListSortRule[]) => void;
  columnOrder: readonly CampaignListDataColumnId[];
  hiddenColumns: ReadonlySet<CampaignListDataColumnId>;
  onToggleColumn: (id: CampaignListDataColumnId) => void;
  onMoveColumn: (id: CampaignListDataColumnId, direction: "up" | "down") => void;
  onReorderColumn: (fromId: CampaignListDataColumnId, toId: CampaignListDataColumnId) => void;
  onResetColumns: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [mobile, setMobile] = useState<CampaignListMobileToolsState>(CAMPAIGN_LIST_MOBILE_TOOLS_CLOSED);
  const pendingPanelRef = useRef<CampaignListMobilePanel | null>(null);
  const openPanelTimerRef = useRef<number | null>(null);
  const sortCount = sort.length;

  const cancelScheduledPanel = () => {
    if (openPanelTimerRef.current == null) return;
    window.clearTimeout(openPanelTimerRef.current);
    openPanelTimerRef.current = null;
  };

  useEffect(
    () => () => {
      if (openPanelTimerRef.current != null) {
        window.clearTimeout(openPanelTimerRef.current);
      }
    },
    [],
  );

  const schedulePanel = (panel: CampaignListMobilePanel) => {
    cancelScheduledPanel();
    openPanelTimerRef.current = window.setTimeout(() => {
      openPanelTimerRef.current = null;
      pendingPanelRef.current = null;
      setMobile((current) => campaignListMobileSetPanel(current, panel));
    }, CAMPAIGN_LIST_MOBILE_PANEL_OPEN_MS);
  };

  const handleOverflowOpenChange = (open: boolean) => {
    if (open) {
      cancelScheduledPanel();
      pendingPanelRef.current = null;
      setMobile((current) => campaignListMobileOpenOverflow(current));
      return;
    }
    const pending = pendingPanelRef.current;
    setMobile((current) => campaignListMobileOverflowClosed(current).next);
    if (pending) schedulePanel(pending);
  };

  const selectOverflowItem = (panel: CampaignListMobilePanel) => {
    pendingPanelRef.current = panel;
    setMobile((current) => campaignListMobileSelectOverflowItem(current, panel));
  };

  return (
    <>
      <div className="hidden shrink-0 items-end gap-2 lg:flex" data-testid="campaign-list-tools-desktop">
        <CampaignsSortPopover sort={sort} onChange={onSortChange} />
        <CampaignsColumnsPopover
          columnOrder={columnOrder}
          hiddenColumns={hiddenColumns}
          onToggle={onToggleColumn}
          onMove={onMoveColumn}
          onReorder={onReorderColumn}
          onReset={onResetColumns}
        />
      </div>
      <div className="relative shrink-0 lg:hidden" data-testid="campaign-list-tools-mobile">
        <DropdownMenu open={mobile.overflowOpen} onOpenChange={handleOverflowOpenChange}>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9"
              data-testid="campaign-list-tools-overflow"
              aria-label={t("campaigns.list.toolsMenu")}
            >
              <MoreHorizontal size={16} aria-hidden="true" />
              {sortCount > 0 ? (
                <span className="text-[0.7rem] font-semibold tabular-nums">{sortCount}</span>
              ) : null}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            onCloseAutoFocus={(event) => {
              if (pendingPanelRef.current != null) event.preventDefault();
            }}
          >
            <DropdownMenuItem
              data-testid="campaign-list-tools-sort"
              onSelect={() => selectOverflowItem("sort")}
            >
              <ArrowUpDown size={14} aria-hidden="true" />
              {sortCount > 0
                ? t("campaigns.list.sortCount", { count: sortCount })
                : t("campaigns.list.sort")}
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="campaign-list-tools-columns"
              onSelect={() => selectOverflowItem("columns")}
            >
              <SlidersHorizontal size={14} aria-hidden="true" />
              {t("campaigns.list.columnsControl")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Popover
          open={mobile.panel != null}
          onOpenChange={(open) => {
            if (!open) {
              cancelScheduledPanel();
              pendingPanelRef.current = null;
              setMobile((current) => campaignListMobileSetPanel(current, null));
            }
          }}
        >
          <PopoverAnchor asChild>
            <span className="pointer-events-none absolute inset-0" aria-hidden="true" />
          </PopoverAnchor>
          {mobile.panel != null ? (
            <PopoverContent
              align="end"
              className="w-[min(22rem,calc(100vw-2rem))] p-2"
              data-testid={
                mobile.panel === "sort"
                  ? "campaign-list-mobile-sort-surface"
                  : "campaign-list-mobile-columns-surface"
              }
            >
              {mobile.panel === "sort" ? (
                <CampaignsSortPanel sort={sort} onChange={onSortChange} />
              ) : (
                <CampaignsColumnsPanel
                  columnOrder={columnOrder}
                  hiddenColumns={hiddenColumns}
                  onToggle={onToggleColumn}
                  onMove={onMoveColumn}
                  onReorder={onReorderColumn}
                  onReset={onResetColumns}
                />
              )}
            </PopoverContent>
          ) : null}
        </Popover>
      </div>
    </>
  );
}
